import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, TextField, toastManager } from '@trove/ui';
import { Fingerprint } from '@phosphor-icons/react';
import type { ProfileIconBackgroundId, ProfileIconId } from '@trove/contracts';
import { getRepositories, putJson } from '../api';
import { EntryActions, EntryLayout } from '../components/EntryLayout';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { BackupPhraseStep, defaultPasskeyIdentity } from '../components/VaultSetup';
import { passkeysSupported, registerPasskey } from '../passkeys';
import { PROFILE_ICON_OPTIONS } from '../profileIcons';
import { useSession } from '../session';
import { useWorkspace } from '../workspace';
import shared from '../styles/shared.module.css';
import styles from './-onboarding.module.css';

type Step = 'profile' | 'backup' | 'passkey';

/**
 * First-run setup: profile, then the backup phrase (which creates the vault), then an optional
 * passkey. The passkey comes last so it can be enrolled to unlock the new vault, not only sign in.
 */
export function OnboardingScreen() {
  const { user } = useSession();
  const { workspace } = useWorkspace();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('profile');
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [icon, setIcon] = useState<ProfileIconId>(user?.icon ?? 'user');
  const [iconBackground] = useState<ProfileIconBackgroundId>(user?.iconBackground ?? 'jade');
  const [saving, setSaving] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  // An account can already own a vault (e.g. setup was interrupted on another device).
  const [repositoryExists, setRepositoryExists] = useState<boolean | null>(null);
  const canPasskeys = passkeysSupported();

  useEffect(() => {
    void getRepositories()
      .then((result) => setRepositoryExists(result.repositories.length > 0))
      .catch(() => setRepositoryExists(false));
  }, []);

  if (!user) return null;

  // Fixed once known so the step count doesn't shift after the vault is created.
  const steps: Step[] = [
    'profile',
    ...(repositoryExists === false ? (['backup'] as const) : []),
    ...(canPasskeys ? (['passkey'] as const) : []),
  ];
  const stepInfo =
    steps.length > 1 ? { current: steps.indexOf(step) + 1, total: steps.length } : undefined;

  const previewName = `${firstName.trim()} ${lastName.trim()}`.trim() || user.displayName;
  const nameReady = Boolean(firstName.trim() && lastName.trim());

  const saveProfile = async (completeOnboarding: boolean) => {
    const result = await putJson<{ user: typeof user }>('/v1/profile', {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      icon,
      iconBackground,
      completeOnboarding,
    });
    queryClient.setQueryData(['session'], { user: result.user });
    await queryClient.invalidateQueries({ queryKey: ['session'] });
  };

  const finish = async () => {
    setSaving(true);
    try {
      await saveProfile(true);
    } catch (cause) {
      toastManager.add({
        title: 'Could not finish setup',
        description: cause instanceof Error ? cause.message : 'Try again.',
      });
      setSaving(false);
    }
  };

  const advanceFrom = async (current: Step) => {
    const next = steps[steps.indexOf(current) + 1];
    if (next) setStep(next);
    else await finish();
  };

  const continueFromProfile = async () => {
    if (!nameReady || repositoryExists === null) return;
    setSaving(true);
    try {
      await saveProfile(false);
      setSaving(false);
      await advanceFrom('profile');
    } catch (cause) {
      toastManager.add({
        title: 'Could not save profile',
        description: cause instanceof Error ? cause.message : 'Try again.',
      });
      setSaving(false);
    }
  };

  const addPasskey = async () => {
    setEnrolling(true);
    try {
      const { label, icon: passkeyIcon } = defaultPasskeyIdentity();
      const result = await registerPasskey(
        label,
        passkeyIcon,
        workspace?.vaultMode === 'locked' && workspace.collectionKey.length > 0
          ? {
              repositoryId: workspace.repositoryId,
              collectionKey: Uint8Array.from(workspace.collectionKey),
            }
          : undefined,
      );
      if (workspace && !result.unlockEnabled) {
        toastManager.add({
          title: 'Passkey added for sign-in',
          description:
            'This passkey provider can’t unlock your files. Keep your backup phrase handy for new devices.',
        });
      }
      await finish();
    } catch (cause) {
      if (!(cause instanceof Error && cause.name === 'NotAllowedError')) {
        toastManager.add({
          title: 'Could not add passkey',
          description: cause instanceof Error ? cause.message : 'Try again.',
        });
      }
    } finally {
      setEnrolling(false);
    }
  };

  if (step === 'backup') {
    return (
      <EntryLayout
        step={stepInfo}
        title="Save your backup phrase"
        description="These 12 words are the only way back into your files if you lose this device. Trove can’t reset them."
      >
        <BackupPhraseStep onCreated={() => advanceFrom('backup')} />
      </EntryLayout>
    );
  }

  if (step === 'passkey') {
    return (
      <EntryLayout
        step={stepInfo}
        eyebrow={
          <span className={styles.stepIcon}>
            <Fingerprint size={20} />
          </span>
        }
        title="Add a passkey"
        description={
          workspace
            ? 'Sign in and unlock your files on new devices with Face ID, Touch ID, or your device PIN — no email link or backup phrase needed.'
            : 'Sign in next time with Face ID, Touch ID, or your device PIN instead of an email link.'
        }
      >
        <EntryActions>
          <Button
            variant="primary"
            disabled={enrolling || saving}
            onClick={() => void addPasskey()}
          >
            {enrolling ? 'Waiting for passkey…' : 'Add passkey'}
          </Button>
          <Button variant="ghost" disabled={enrolling || saving} onClick={() => void finish()}>
            {saving && !enrolling ? 'Finishing…' : 'Skip for now'}
          </Button>
        </EntryActions>
        <p className={styles.hint}>You can add or remove passkeys later in Settings.</p>
      </EntryLayout>
    );
  }

  return (
    <EntryLayout
      step={stepInfo}
      title="Set up your profile"
      description="Your name appears on changes you save and in the account menu."
    >
      <form
        className={shared.formStack}
        onSubmit={(event) => {
          event.preventDefault();
          void continueFromProfile();
        }}
      >
        <div className={styles.nameRow}>
          <TextField
            label="First name"
            autoComplete="given-name"
            autoFocus
            value={firstName}
            onValueChange={setFirstName}
          />
          <TextField
            label="Last name"
            autoComplete="family-name"
            value={lastName}
            onValueChange={setLastName}
          />
        </div>
        <div className={styles.iconField}>
          <div className={styles.iconLabel}>Avatar</div>
          <div className={styles.iconRow}>
            <ProfileAvatar
              user={{ displayName: previewName, icon, iconBackground }}
              size="md"
              className={styles.previewAvatar}
            />
            <div className={styles.iconGrid} role="radiogroup" aria-label="Avatar">
              {PROFILE_ICON_OPTIONS.map(({ id, label, emoji }) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={icon === id}
                  aria-label={label}
                  className={`${styles.iconOption} ${icon === id ? styles.iconOptionActive : ''}`}
                  onClick={() => setIcon(id)}
                >
                  <span aria-hidden="true">{emoji}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <EntryActions>
          <Button
            type="submit"
            variant="primary"
            disabled={!nameReady || saving || repositoryExists === null}
          >
            {saving ? 'Saving…' : 'Continue'}
          </Button>
        </EntryActions>
      </form>
    </EntryLayout>
  );
}
